import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon, CheckIcon, XMarkIcon,
  CloudArrowUpIcon, CheckCircleIcon,
  FilmIcon, DocumentTextIcon, LinkIcon,
  ExclamationCircleIcon, EyeIcon, HeartIcon, ShareIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { useContentsNew } from "../hooks/useContentsNew";
import { useLanguage } from "../../../context/LanguageContext";

const INPUT =
  "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 bg-white outline-none transition-all focus:border-[#b2d12e] focus:ring-4 focus:ring-[#b2d12e]/20 placeholder-gray-400";

const DESC_MAX = 500;

export default function ContentsNew() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const {
    id, categories, loading, uploadProgress,
    formData, setField,
    videoPreview, videoFileName, videoFileSize, videoError, uploadError,
    handleVideoUpload, clearVideo, handleSubmit, retrySubmit,
  } = useContentsNew();

  const isEditing = Boolean(id);
  const isUploading = loading && Boolean(formData.video) && uploadProgress < 100;
  const uploadDone = uploadProgress === 100 && loading;

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleVideoUpload(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleVideoUpload(file);
    e.target.value = "";
  };

  const hasVideo = Boolean(formData.video) || Boolean(videoPreview);
  const descLen = formData.description.length;
  const isFormReady = Boolean(formData.title && formData.description && formData.category);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 pt-24 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">

        {/* ── Header ── */}
        <div className="mb-8">
          <button
            type="button"
            onClick={() => navigate("/admin/contents")}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors group"
          >
            <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            {t("admin.contentsNew.backToContent")}
          </button>

          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#b2d12e]/15 flex items-center justify-center flex-shrink-0">
              <FilmIcon className="w-6 h-6 text-[#7a9400]" />
            </div>
            <div>
              <p className="text-xs font-semibold text-[#b2d12e] uppercase tracking-wider mb-0.5">
                {isEditing ? t("admin.contentsNew.editingLabel") : t("admin.contentsNew.newContentLabel")}
              </p>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? t("admin.contentsNew.editContentTitle") : t("admin.contentsNew.createContentTitle")}
              </h1>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* ── Basic Information ── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-2.5 px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-transparent">
              <DocumentTextIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">{t("admin.contentsNew.basicInformation")}</h2>
            </div>

            <div className="p-6 space-y-5">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {t("admin.contentsNew.titleLabel")} <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setField("title", e.target.value)}
                  placeholder={t("admin.contentsNew.titlePlaceholder")}
                  maxLength={255}
                  required
                  className={INPUT}
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {t("admin.contentsNew.categoryLabel")} <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <select
                    value={formData.category ?? ""}
                    onChange={(e) => setField("category", e.target.value)}
                    required
                    className={`${INPUT} appearance-none pr-10 cursor-pointer`}
                  >
                    <option value="" disabled>{t("admin.contentsNew.selectCategory")}</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-semibold text-gray-700">
                    {t("admin.contentsNew.descriptionLabel")} <span className="text-red-400">*</span>
                  </label>
                  <span className={`text-xs font-medium ${descLen > DESC_MAX ? "text-red-500" : "text-gray-400"}`}>
                    {descLen} / {DESC_MAX}
                  </span>
                </div>
                <textarea
                  value={formData.description}
                  onChange={(e) => setField("description", e.target.value)}
                  rows={4}
                  placeholder={t("admin.contentsNew.descriptionPlaceholder")}
                  required
                  className={`${INPUT} resize-none`}
                />
              </div>
            </div>
          </div>

          {/* ── Media ── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-2.5 px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-transparent">
              <FilmIcon className="w-4 h-4 text-purple-500 flex-shrink-0" />
              <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">{t("admin.contentsNew.media")}</h2>
            </div>

            <div className="p-6 space-y-5">

              {/* Upload zone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t("admin.contentsNew.uploadVideo")}</label>

                {!hasVideo ? (
                  <>
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                      className={`relative flex flex-col items-center justify-center gap-3 p-10 rounded-2xl border-2 border-dashed cursor-pointer transition-all duration-200 ${
                        videoError
                          ? "border-red-300 bg-red-50"
                          : isDragging
                          ? "border-[#b2d12e] bg-[#b2d12e]/8 scale-[1.01]"
                          : "border-gray-200 bg-gray-50 hover:border-[#b2d12e]/60 hover:bg-[#b2d12e]/5"
                      }`}
                    >
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${
                        videoError ? "bg-red-100" : isDragging ? "bg-[#b2d12e]/20" : "bg-white border border-gray-200"
                      }`}>
                        {videoError
                          ? <ExclamationCircleIcon className="w-7 h-7 text-red-500" />
                          : <CloudArrowUpIcon className={`w-7 h-7 ${isDragging ? "text-[#7a9400]" : "text-gray-400"}`} />
                        }
                      </div>

                      <div className="text-center">
                        <p className={`text-sm font-semibold ${
                          videoError ? "text-red-600" : isDragging ? "text-[#7a9400]" : "text-gray-700"
                        }`}>
                          {isDragging ? t("admin.contentsNew.dropToUpload") : videoError ? t("admin.contentsNew.tryDifferentFile") : t("admin.contentsNew.dropOrBrowse")}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">{t("admin.contentsNew.videoFormatsHint")}</p>
                      </div>

                      {videoError && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 rounded-lg">
                          <ExclamationCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <p className="text-xs font-medium text-red-700">{videoError}</p>
                        </div>
                      )}
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska"
                      className="hidden"
                      disabled={loading}
                      onChange={handleFileChange}
                    />
                  </>
                ) : (
                  <div className="rounded-2xl border border-gray-200 overflow-hidden">
                    {/* File info bar */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-[#b2d12e]/8 border-b border-[#b2d12e]/20">
                      <div className="w-8 h-8 rounded-lg bg-[#b2d12e]/20 flex items-center justify-center flex-shrink-0">
                        <FilmIcon className="w-4 h-4 text-[#7a9400]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {videoFileName ?? t("admin.contentsNew.selectedVideoFallback")}
                        </p>
                        {videoFileSize && (
                          <p className="text-xs text-gray-400">{videoFileSize}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={clearVideo}
                        className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title={t("admin.contentsNew.removeVideo")}
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Video preview */}
                    {videoPreview && (
                      <video
                        key={videoPreview}
                        src={videoPreview}
                        controls
                        className="w-full max-h-72 bg-black object-contain"
                      />
                    )}
                  </div>
                )}

                {/* Upload progress */}
                {isUploading && (
                  <div className="mt-3 space-y-1.5">
                    <div className="flex items-center justify-between text-xs font-medium">
                      <span className="text-gray-500">{t("admin.contentsNew.uploadingVideo")}</span>
                      <span className="text-[#7a9400] font-bold">{uploadProgress}%</span>
                    </div>
                    <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#b2d12e] to-[#a0c827] rounded-full transition-all duration-200"
                        style={{ width: `${Math.max(uploadProgress, 2)}%` }}
                      />
                    </div>
                  </div>
                )}

                {uploadDone && (
                  <div className="mt-3 flex items-center gap-2 px-3 py-2.5 bg-green-50 border border-green-200 rounded-xl">
                    <CheckCircleIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-green-700">{t("admin.contentsNew.videoUploadedSuccess")}</span>
                  </div>
                )}

                {uploadError && !loading && (
                  <div className="mt-3 flex items-start gap-2.5 px-3 py-3 bg-red-50 border border-red-200 rounded-xl">
                    <ExclamationCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-red-700">{t("admin.contentsNew.uploadFailedLabel")}</p>
                      <p className="text-xs text-red-600 mt-0.5 break-words">{uploadError}</p>
                    </div>
                    <button
                      type="button"
                      onClick={retrySubmit}
                      className="shrink-0 text-xs font-bold text-red-700 hover:text-red-900 underline"
                    >
                      {t("common.retry")}
                    </button>
                  </div>
                )}
              </div>

              {/* OR divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{t("admin.contentsNew.orUseUrl")}</span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>

              {/* External URL */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("admin.contentsNew.externalVideoUrlLabel")}</label>
                <div className="relative">
                  <LinkIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="url"
                    value={formData.video_url || ""}
                    onChange={(e) => setField("video_url", e.target.value)}
                    placeholder={t("admin.contentsNew.videoUrlPlaceholder")}
                    disabled={hasVideo}
                    className={`${INPUT} pl-10 ${hasVideo ? "opacity-40 cursor-not-allowed bg-gray-50" : ""}`}
                  />
                </div>
                {hasVideo && (
                  <p className="mt-1.5 text-xs text-gray-400">{t("admin.contentsNew.removeVideoToUseUrl")}</p>
                )}
              </div>

            </div>
          </div>

          {/* ── Metrics ── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-2.5 px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-green-50 to-transparent">
              <ChartBarIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
              <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wide">{t("admin.contentsNew.metrics")}</h2>
            </div>
            <div className="p-6 grid grid-cols-3 gap-4">
              {([
                { key: "views",  label: t("admin.contentsNew.views"),  icon: <EyeIcon   className="w-4 h-4 text-gray-400" /> },
                { key: "likes",  label: t("admin.contentsNew.likes"),  icon: <HeartIcon  className="w-4 h-4 text-red-400"  /> },
                { key: "shares", label: t("admin.contentsNew.shares"), icon: <ShareIcon  className="w-4 h-4 text-blue-400" /> },
              ] as const).map(({ key, label, icon }) => (
                <div key={key}>
                  <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-1.5">
                    {icon} {label}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.metrics?.[key] ?? 0}
                    onChange={(e) =>
                      setField("metrics", {
                        ...(formData.metrics ?? { views: 0, likes: 0, shares: 0 }),
                        [key]: Math.max(0, Number(e.target.value)),
                      })
                    }
                    className={INPUT}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* ── Sticky Action Bar ── */}
          <div className="sticky bottom-4 z-10">
            <div className="bg-white/90 backdrop-blur-md border border-gray-200 rounded-2xl shadow-lg px-5 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${isFormReady ? "bg-[#b2d12e]" : "bg-gray-300"}`} />
                <span className="text-xs text-gray-500">
                  {isFormReady ? t("admin.contentsNew.readyToPublish") : t("admin.contentsNew.fillRequiredFields")}
                </span>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/admin/contents")}
                  disabled={loading}
                  className="px-5 py-2.5 text-sm font-semibold text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-40"
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={loading || !isFormReady}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#b2d12e] hover:bg-[#a0c827] text-gray-900 text-sm font-bold rounded-xl transition-all hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-none"
                >
                  {loading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      {isEditing ? t("admin.contentsNew.updating") : t("admin.contentsNew.publishing")}
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      {isEditing ? t("admin.contentsNew.updateContent") : t("admin.contentsNew.publishContent")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

        </form>
      </div>
    </div>
  );
}
